import nacl from 'tweetnacl';
import { PublicKey } from '@solana/web3.js';

/**
 * Message Signing and Verification Utilities
 *
 * Used for wallet ownership verification (e.g., setting primary wallet)
 * without requiring full wallet connection.
 */

/**
 * Creates a standard wallet validation message
 * This message will be signed by the user to prove wallet ownership
 *
 * @param walletAddress - The wallet public key to validate
 * @returns Formatted message string for signing
 */
export const createWalletValidationMessage = (walletAddress: string): string => {
  const timestamp = Date.now();

  return `Tweetonium Wallet Validation

I am verifying ownership of this wallet address:
${walletAddress}

Timestamp: ${timestamp}

This signature proves I control this wallet without sharing my private key.`;
};

/**
 * Verifies that a signature was created by the expected wallet
 *
 * @param message - The original message that was signed
 * @param signature - The signature bytes from the wallet
 * @param publicKey - The expected signer's public key
 * @returns true if signature is valid, false otherwise
 */
export const verifySignature = (
  message: string,
  signature: Uint8Array,
  publicKey: PublicKey
): boolean => {
  try {
    // Convert message string to bytes
    const messageBytes = new TextEncoder().encode(message);

    // Verify signature using nacl
    // Note: Solana wallets use Ed25519 detached signatures
    const isValid = nacl.sign.detached.verify(
      messageBytes,
      signature,
      publicKey.toBytes()
    );

    return isValid;
  } catch (error) {
    console.error('[Signature Verification] Failed:', error);
    return false;
  }
};

/**
 * Converts a Uint8Array to a hex string for display/debugging
 */
export const signatureToHex = (signature: Uint8Array): string => {
  return Array.from(signature)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Converts a hex string back to Uint8Array
 */
export const hexToSignature = (hex: string): Uint8Array => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
};
